import React, { useState, useMemo } from "react";
import CollapsibleDataCard from "@/components/play/CollapsibleDataCard";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
// NOTE: useGetTransactions removed - now using dedicated endpoint hooks
import { useGetTransactionHistoryXP, TimeFilter } from "@/hooks/useGetTransactionHistoryXP";
import { useGetTransactionHistoryGamingXP } from "@/hooks/useGetTransactionHistoryGamingXP";
import { useGetTransactionHistoryFuel } from "@/hooks/useGetTransactionHistoryFuel";
import { useGetTransactionHistoryFood } from "@/hooks/useGetTransactionHistoryFood";
import { useGetTransactionHistoryLootShop } from "@/hooks/useGetTransactionHistoryLootShop";
import { useGetTransactionHistoryLootboxes } from "@/hooks/useGetTransactionHistoryLootboxes";
import { useGetTransactionHistoryRedemptions } from "@/hooks/useGetTransactionHistoryRedemptions";
import { FaHistory, FaFire, FaGift, FaClock } from "react-icons/fa";
import { RiVipDiamondLine } from "react-icons/ri";
import { MdCardGiftcard, MdReceipt } from "react-icons/md";
import { BiPurchaseTag } from "react-icons/bi";
import { IoCheckmarkCircle, IoGameController } from "react-icons/io5";
import { GiBullseye } from "react-icons/gi";
import { Token } from "@devour/client";
import "./TransactionHistoryCard.scss";

interface TransactionHistoryCardProps {
    fullToken?: Token;
    userId?: string;
}

type TransactionFilter = "all" | "xp" | "quests" | "rewards" | "fuel" | "purchases" | "lootbox" | "gaming" | "orders";

const TransactionHistoryCard: React.FC<TransactionHistoryCardProps> = ({ fullToken, userId }) => {
    const { preferences, setTransactionFilter } = useCockpitPreferences();
    const [filter, setFilter] = useState<TransactionFilter>((preferences.transactionFilter as TransactionFilter) || "xp");
    const [timeFilter, setTimeFilter] = useState<TimeFilter>("day");

    // Fetch XP transactions from user level data
    const { data: userLevelData, isLoading: levelLoading } = useGetUserLevel(
        fullToken,
        userId,
        !fullToken || !userId,
    );

    // NOTE: Old useGetTransactions removed - now using dedicated endpoint hooks


    // Fetch XP transactions with time filtering
    const { data: xpTransactionData, isLoading: xpTransactionLoading, error: xpTransactionError } = useGetTransactionHistoryXP(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "xp" || filter === "quests",
        },
    );

    // Fetch Gaming XP transactions with time filtering
    const { data: gamingXpTransactionData, isLoading: gamingXpTransactionLoading, error: gamingXpTransactionError } = useGetTransactionHistoryGamingXP(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "gaming",
        },
    );

    // Fetch Fuel transactions with time filtering
    const { data: fuelTransactionData, isLoading: fuelTransactionLoading, error: fuelTransactionError } = useGetTransactionHistoryFuel(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "fuel",
        },
    );

    // Fetch Food transactions with time filtering
    const { data: foodTransactionData, isLoading: foodTransactionLoading, error: foodTransactionError } = useGetTransactionHistoryFood(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "orders",
        },
    );

    // Fetch LootShop transactions with time filtering
    const { data: lootshopTransactionData, isLoading: lootshopTransactionLoading, error: lootshopTransactionError } = useGetTransactionHistoryLootShop(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "orders",
        },
    );

    // Fetch Lootbox PURCHASES with time filtering (keeping for potential future use)
    const { data: lootboxPurchaseData, isLoading: lootboxPurchaseLoading, error: lootboxPurchaseError } = useGetTransactionHistoryLootboxes(
        fullToken?.token,
        {
            timeFilter,
            enabled: false, // Disabled - we now show redemptions instead
        },
    );

    // Fetch Lootbox REDEMPTIONS (prize reveals) with time filtering
    const { data: lootboxRedemptionData, isLoading: lootboxRedemptionLoading, error: lootboxRedemptionError } = useGetTransactionHistoryRedemptions(
        fullToken?.token,
        {
            timeFilter,
            enabled: filter === "all" || filter === "lootbox",
        },
    );

    if (xpTransactionError) {
        console.error("XP Transaction Hook Error:", xpTransactionError);
    }

    if (gamingXpTransactionError) {
        console.error("Gaming XP Transaction Hook Error:", gamingXpTransactionError);
    }

    if (fuelTransactionError) {
        console.error("Fuel Transaction Hook Error:", fuelTransactionError);
    }

    if (foodTransactionError) {
        console.error("Food Transaction Hook Error:", foodTransactionError);
    }

    if (lootshopTransactionError) {
        console.error("LootShop Transaction Hook Error:", lootshopTransactionError);
    }

    if (lootboxPurchaseError) {
        console.error("Lootbox Purchase Hook Error:", lootboxPurchaseError);
    }

    if (lootboxRedemptionError) {
        console.error("Lootbox Redemption Hook Error:", lootboxRedemptionError);
    }


    // Handle filter change
    const handleFilterChange = (newFilter: TransactionFilter) => {
        setFilter(newFilter);
        setTransactionFilter(newFilter);
    };

    // Format XP description - moved before useMemo to avoid hoisting issue
    const formatXPDescription = (subject: string): string => {
        if (!subject) return "XP Transaction";

        const formatted = subject
            .replace(/_/g, " ")
            .replace(/OVERWOLF/g, "Gaming")
            .replace(/GUILD/g, "Guild")
            .replace(/BRAND/g, "Brand")
            .toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase());

        return formatted;
    };

    // Format transaction description
    const formatTransactionDescription = (subject: string): string => {
        if (!subject) return "Transaction";

        // Convert specific subjects to user-friendly names
        if (subject === "FIAT_PURCHASE_DPAY") return "Fuel Load";
        if (subject === "MINT_NFT_PURCHASE") return "LootBox Purchase";
        if (subject === "LOOTBOX_BURNED") return "LootBox Opening";
        if (subject === "MENU_ORDER_PAYMENT") return "Menu Order Payment";

        // Default formatting for other subjects
        return subject
            .replace(/_/g, " ")
            .replace(/DPAY/g, "Fuel")
            .toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    // Helper function to check if date is within time filter
    const isWithinTimeFilter = (dateStr: string): boolean => {
        if (timeFilter === "all") return true;

        const date = new Date(dateStr);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (timeFilter) {
            case "day":
                return date >= today;
            case "week":
                const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                return date >= weekAgo;
            case "month":
                const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                return date >= monthAgo;
            case "year":
                const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
                return date >= yearAgo;
            default:
                return true;
        }
    };

    // Combine and filter transactions
    const filteredTransactions = useMemo(() => {
        const allTransactions = [];

        // Add XP transactions from new time-filtered endpoint
        if (xpTransactionData?.transactions && (filter === "all" || filter === "xp" || filter === "quests")) {
            xpTransactionData.transactions.forEach(t => {
                // Check if this is a time-based reward
                const isTimeReward = t.subject?.includes("TIME_REWARD") ||
                           t.subject?.includes("TIME_QUEST_REWARD");

                // Check if this is a gaming event reward (actual quest completions in Overwolf)
                const isGamingEvent = t.subject?.includes("OVERWOLF_EVENT_REWARD");

                // Check if this is a quest or challenge completion (but NOT time rewards or gaming events)
                const isQuest = !isGamingEvent && !isTimeReward &&
                       (t.subject?.includes("QUEST") || t.subject?.includes("CHALLENGE"));

                // XP filter shows ALL XP transactions, Quests filter shows only quest-related ones
                if (filter === "all" ||
            filter === "xp" ||
            filter === "quests" && isQuest) {
                    // Format description and fix any Dpay references
                    let xpDescription = formatXPDescription(t.subject);
                    if (xpDescription && xpDescription.toLowerCase().includes("dpay")) {
                        xpDescription = xpDescription.replace(/dpay/gi, "Fuel");
                    }

                    // Determine the appropriate category
                    let category = "xp"; // Default to XP
                    if (isTimeReward) {
                        category = "time"; // Time rewards get clock icon
                    } else if (isGamingEvent) {
                        category = "gaming"; // Gaming event rewards get gaming controller icon
                    } else if (isQuest) {
                        category = "quest"; // Regular quests keep their checkmark
                    }

                    allTransactions.push({
                        id: `xp-${t.id}`,
                        type: "xp",
                        category: category,
                        amount: t.amount,
                        transactionType: t.type, // DEBIT or CREDIT
                        subject: t.subject,
                        createdAt: t.createdAt,
                        description: xpDescription,
                    });
                }
            });
        }

        // NOTE: Old Fuel transaction logic removed - now using dedicated Fuel endpoint (useGetTransactionHistoryFuel)

        // Add Food and LootShop Orders from new time-filtered endpoints

        // Add Food transactions from new time-filtered endpoint
        if (foodTransactionData?.transactions && (filter === "all" || filter === "orders")) {
            foodTransactionData.transactions.forEach((t, index) => {
                allTransactions.push({
                    id: `food-order-${t.id}`,
                    type: "orders",
                    category: "orders",
                    amount: t.amount, // Amount in cents
                    amountFormatted: t.amountFormatted, // Formatted USD amount
                    transactionType: "DEBIT", // Orders are always debits
                    subject: t.description,
                    restaurant: t.restaurant,
                    itemCount: t.itemCount,
                    paymentMethod: t.paymentMethod,
                    fuelUsed: t.fuelUsed,
                    createdAt: t.createdAt,
                    description: t.description, // Already formatted from backend
                });
            });
        }

        // Add LootShop transactions from new time-filtered endpoint
        if (lootshopTransactionData?.transactions && (filter === "all" || filter === "orders")) {
            lootshopTransactionData.transactions.forEach((t, index) => {
                allTransactions.push({
                    id: `lootshop-order-${t.id}`,
                    type: "orders",
                    category: "orders",
                    amount: t.amount, // Amount in cents
                    amountFormatted: t.amountFormatted, // Formatted USD amount
                    transactionType: "DEBIT", // Orders are always debits
                    subject: t.description,
                    itemCount: t.itemCount,
                    paymentMethod: t.paymentMethod,
                    createdAt: t.createdAt,
                    description: t.description, // Already formatted from backend
                });
            });
        }

        // Add Lootbox REDEMPTIONS (prize reveals) from new endpoint
        if (lootboxRedemptionData?.transactions && (filter === "all" || filter === "lootbox")) {
            lootboxRedemptionData.transactions.forEach((t, index) => {
                allTransactions.push({
                    id: `lootbox-redemption-${t.id}`,
                    type: "lootbox",
                    category: "lootbox",
                    amount: t.prizeValue || 0, // Prize value if available
                    transactionType: "CREDIT", // Redemptions are credits (you receive prizes)
                    subject: `${t.lootboxName} - ${t.prizeName}`,
                    lootboxName: t.lootboxName,
                    prizeName: t.prizeName,
                    prizeType: t.prizeType,
                    prizeImage: t.prizeImage,
                    createdAt: t.burnedAt || t.createdAt,
                    description: t.description || `${t.lootboxName} - ${t.prizeName}`, // Format: "LootBox Name - Prize Name"
                });
            });
        }

        /*
         * Add Gaming XP transactions from new time-filtered endpoint
         * Only add when specifically filtering for gaming (not 'all' since XP endpoint already includes gaming)
         */
        if (gamingXpTransactionData?.transactions && filter === "gaming") {
            gamingXpTransactionData.transactions.forEach((t, index) => {
                // Determine the appropriate category based on subject
                let category = "gaming"; // Default to gaming

                // Check if this is a time-based reward
                if (t.subject?.includes("TIME_REWARD") || t.subject?.includes("TIME_QUEST_REWARD")) {
                    category = "time"; // Time rewards get clock icon
                } else if (t.subject?.includes("EVENT_REWARD")) {
                    category = "gaming"; // Event rewards get gaming controller icon
                }

                allTransactions.push({
                    id: `gaming-xp-${t.id}`,
                    type: "gaming",
                    category: category,
                    amount: t.amount,
                    transactionType: t.type, // DEBIT or CREDIT
                    subject: t.subject,
                    createdAt: t.createdAt,
                    description: t.description, // Already formatted from backend with game names
                });
            });
        }

        // Add Fuel transactions from new time-filtered endpoint
        if (fuelTransactionData?.transactions && fuelTransactionData.transactions.length > 0 && (filter === "all" || filter === "fuel")) {
            fuelTransactionData.transactions.forEach((t, index) => {
                // Use formatTransactionDescription to handle legacy naming
                let description = formatTransactionDescription(t.subject || t.description);

                // Additional check for any remaining "Dpay" references
                if (description && description.toLowerCase().includes("dpay")) {
                    description = description.replace(/dpay/gi, "Fuel");
                }

                allTransactions.push({
                    id: `fuel-${t.id}`,
                    type: "fuel",
                    category: "fuel",
                    amount: t.fuelAmount, // Use fuel amount for display
                    fiatAmount: t.fiatAmount, // Store fiat amount separately
                    fiatAmountFormatted: t.fiatAmountFormatted, // Store formatted fiat amount
                    transactionType: t.type, // DEBIT or CREDIT
                    subject: t.subject,
                    createdAt: t.createdAt,
                    description: description, // Fixed naming from Dpay to Fuel
                });
            });
        }

        // Sort by date (newest first) and show up to 50 with scrolling
        return allTransactions.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 50); // Show up to 50 transactions with scrolling
    }, [xpTransactionData, gamingXpTransactionData, fuelTransactionData, foodTransactionData, lootshopTransactionData, lootboxRedemptionData, filter, timeFilter]);

    // Format date
    const formatDate = (date: string): string => {
        const d = new Date(date);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        if (diffHours < 1) return "Just now";
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffHours < 48) return "Yesterday";
        if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;

        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    // Get icon for transaction type
    const getTransactionIcon = (category: string) => {
        switch (category) {
            case "quest":
                return <IoCheckmarkCircle className="transaction-icon quest" />;
            case "time":
                return <FaClock className="transaction-icon time" />;
            case "gaming":
                return <IoGameController className="transaction-icon gaming" />;
            case "xp":
                return <RiVipDiamondLine className="transaction-icon xp" />;
            case "reward":
                return <MdCardGiftcard className="transaction-icon reward" />;
            case "fuel":
                return <FaFire className="transaction-icon fuel" />;
            case "purchase":
                return <BiPurchaseTag className="transaction-icon purchase" />;
            case "lootbox":
                return <FaGift className="transaction-icon lootbox" />;
            default:
                return <FaHistory className="transaction-icon" />;
        }
    };

    const isLoading = levelLoading || xpTransactionLoading || gamingXpTransactionLoading || fuelTransactionLoading || foodTransactionLoading || lootshopTransactionLoading || lootboxRedemptionLoading;

    return (
        <CollapsibleDataCard
            id="transaction-history-card"
            title="Transaction History"
            icon={<FaHistory />}
            defaultExpanded={false}
            isLoading={isLoading}
            hasData={filteredTransactions.length > 0}
            className="transaction-history-card-wrapper"
        >
            <div className="transaction-history-card">
                {/* Time Filter Buttons - D/W/M/Y/All */}
                <div className="transaction-history-card__time-filters">
                    <button
                        className={`transaction-history-card__time-filter ${timeFilter === "day" ? "active" : ""}`}
                        onClick={() => setTimeFilter("day")}
                        title="Today"
                    >
                        D
                    </button>
                    <button
                        className={`transaction-history-card__time-filter ${timeFilter === "week" ? "active" : ""}`}
                        onClick={() => setTimeFilter("week")}
                        title="This Week"
                    >
                        W
                    </button>
                    <button
                        className={`transaction-history-card__time-filter ${timeFilter === "month" ? "active" : ""}`}
                        onClick={() => setTimeFilter("month")}
                        title="This Month"
                    >
                        M
                    </button>
                    <button
                        className={`transaction-history-card__time-filter ${timeFilter === "year" ? "active" : ""}`}
                        onClick={() => setTimeFilter("year")}
                        title="This Year"
                    >
                        Y
                    </button>
                    <button
                        className={`transaction-history-card__time-filter ${timeFilter === "all" ? "active" : ""}`}
                        onClick={() => setTimeFilter("all")}
                        title="All Time"
                    >
                        All
                    </button>
                </div>

                {/* Transaction Type Filters */}
                <div className="transaction-history-card__filters">
                    <button
                        className={`transaction-history-card__filter icon-only ${filter === "xp" ? "active" : ""}`}
                        onClick={() => handleFilterChange("xp")}
                        title="XP Transactions"
                    >
                        <RiVipDiamondLine />
                    </button>
                    <button
                        className={`transaction-history-card__filter icon-only ${filter === "gaming" ? "active" : ""}`}
                        onClick={() => handleFilterChange("gaming")}
                        title="Gaming XP Transactions"
                    >
                        <IoGameController />
                    </button>
                    <button
                        className={`transaction-history-card__filter icon-only ${filter === "fuel" ? "active" : ""}`}
                        onClick={() => handleFilterChange("fuel")}
                        title="Fuel Transactions"
                    >
                        <FaFire />
                    </button>
                    <button
                        className={`transaction-history-card__filter icon-only ${filter === "orders" ? "active" : ""}`}
                        onClick={() => handleFilterChange("orders")}
                        title="Food & LootShop Orders"
                    >
                        <MdReceipt />
                    </button>
                    <button
                        className={`transaction-history-card__filter icon-only ${filter === "lootbox" ? "active" : ""}`}
                        onClick={() => handleFilterChange("lootbox")}
                        title="LootBox Rewards"
                    >
                        <FaGift />
                    </button>
                    <button
                        className={`transaction-history-card__filter show-text ${filter === "all" ? "active" : ""}`}
                        onClick={() => handleFilterChange("all")}
                        title="All Transactions"
                    >
                        All
                    </button>
                </div>

                <div className="transaction-history-card__list">
                    {isLoading
                        ? <div className="transaction-history-card__empty">
                            Loading...
                        </div>
                        : filteredTransactions.length === 0
                            ? <div className="transaction-history-card__empty">
                                No transactions found
                            </div>
                            : filteredTransactions.map(transaction =>
                                <div key={transaction.id} className="transaction-history-card__item">
                                    {getTransactionIcon(transaction.category)}
                                    <div className="transaction-history-card__info">
                                        <span className="transaction-history-card__description">
                                            {transaction.description}
                                        </span>
                                        <span className="transaction-history-card__date">
                                            {formatDate(transaction.createdAt)}
                                        </span>
                                    </div>
                                    <span className={`transaction-history-card__amount ${transaction.transactionType === "CREDIT" ? "negative" : "positive"}`}>
                                        {transaction.type === "lootbox" ? "" : transaction.transactionType === "CREDIT" ? "-" : "+"}
                                        {transaction.type === "lootbox" ? ""
                                            : transaction.type === "orders" ? `$${((transaction.amount || 0) / 100).toFixed(2)}`
                                                : (transaction.originalAmount || transaction.amount || 0).toLocaleString()}
                                        {transaction.type === "xp" ? " XP"
                                            : transaction.type === "orders" ? ""
                                                : transaction.type === "fuel" ? " Fuel" : ""}
                                    </span>
                                </div>)
                    }
                </div>
            </div>
        </CollapsibleDataCard>
    );
};

export default React.memo(TransactionHistoryCard);